import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { streamChatResponse, saveChatMessage, loadChatHistory as loadDBHistory } from '../services/chatService';
import { saveConversationToWiki } from '../services/wikiService';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    const WELCOME_MESSAGE = {
        id: 'welcome',
        role: 'assistant',
        content: "Hello! I am your **b2b.codes Business Intelligence Assistant**. I specialize in data-driven marketing, SEO strategy, and business systems optimization. \n\nHow can I help you grow your business today?",
        timestamp: new Date()
    };

    // Load history on mount
    useEffect(() => {
        const loadHistory = async () => {
            if (historyLoaded) return;

            setIsLoading(true);
            try {
                console.log('📬 [ChatProvider] Loading history...');
                const { data: history } = await loadDBHistory();

                if (history && history.length > 0) {
                    const formatted = history.map(msg => ({
                        id: msg.id,
                        role: msg.message_role,
                        content: msg.message_content,
                        timestamp: new Date(msg.created_at)
                    }));

                    // CRITICAL: Only apply history if we don't have an active conversation
                    // This prevents overwriting a fresh user message if the DB fetch is slow
                    setMessages(prev => {
                        if (prev.length <= 1) {
                            console.log(`✅ [ChatProvider] Applying ${formatted.length} messages from DB`);
                            return formatted;
                        }
                        console.log('⏳ [ChatProvider] Active session detected, skipping history overwrite');
                        return prev;
                    });
                } else {
                    // Fallback to local storage if DB is empty
                    const localBackup = localStorage.getItem('b2b_chat_local_backup');
                    if (localBackup) {
                        const parsed = JSON.parse(localBackup);
                        const formattedBackup = parsed.map(m => ({ ...m, timestamp: new Date(m.timestamp) }));

                        setMessages(prev => {
                            if (prev.length <= 1) {
                                console.log('✅ [ChatProvider] Applying local storage fallback');
                                return formattedBackup;
                            }
                            return prev;
                        });
                    } else if (messages.length === 0) {
                        // If totally new, add welcome
                        setMessages([WELCOME_MESSAGE]);
                    }
                }
            } catch (err) {
                console.error('Failed to load chat history:', err);
                if (messages.length === 0) setMessages([WELCOME_MESSAGE]);
            } finally {
                setIsLoading(false);
                setHistoryLoaded(true);
            }
        };

        loadHistory();
    }, [historyLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persistent backup to local storage
    useEffect(() => {
        if (messages.length > 1) { // Don't backup if just welcome
            localStorage.setItem('b2b_chat_local_backup', JSON.stringify(messages));
        }
    }, [messages]);

    const sendMessage = useCallback(async (text, context = {}, options = {}) => {
        if (!text?.trim() || isStreaming) return;

        const newUserMessage = {
            id: Date.now(),
            role: 'user',
            content: text.trim(),
            timestamp: new Date()
        };

        // Prepare local array for immediate logic use (avoid state closure issues)
        const updatedMessagesForAI = [...messages, newUserMessage];

        setMessages(prev => [...prev, newUserMessage]);
        setIsStreaming(true);
        setError(null);

        // 1. Initial save of user message
        saveChatMessage({
            role: 'user',
            content: text.trim(),
            categoryId: options.categoryId
        }).catch(e => console.warn('Background save for user msg failed:', e));

        // 2. Prepare assistant placeholder
        const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: '',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);

        let fullResponse = '';

        try {
            const assessmentStr = context.assessmentContext ? `\nUser Assessment Data: ${JSON.stringify(context.assessmentContext)}` : '';
            const systemContext = `You are a professional B2B Growth Consultant for b2b.codes. 
Your primary goal is to help users optimize their business systems while strategically recommending our vetted partners.
Context: ${options.contextOverride || context.source || 'General inquiry'}${assessmentStr}`;

            await streamChatResponse(
                updatedMessagesForAI,
                systemContext,
                (chunk) => {
                    fullResponse += chunk;
                    setMessages(prev => {
                        const updated = [...prev];
                        const last = updated[updated.length - 1];
                        if (last && last.role === 'assistant') {
                            last.content = fullResponse;
                        }
                        return updated;
                    });
                },
                options
            );

            // 3. Final background saves
            setIsSaving(true);
            try {
                const finalMessagesForWiki = [...updatedMessagesForAI, { role: 'assistant', content: fullResponse }];
                await Promise.all([
                    saveChatMessage({
                        role: 'assistant',
                        content: fullResponse || 'No response generated.',
                        categoryId: options.categoryId
                    }),
                    fullResponse ? saveConversationToWiki({
                        messages: finalMessagesForWiki,
                        context: { ...context, categoryId: options.categoryId }
                    }) : Promise.resolve()
                ]);
            } catch (saveError) {
                console.warn('Background save error:', saveError);
            } finally {
                setIsSaving(false);
            }

        } catch (err) {
            console.error('Chat error:', err);

            // Surface specific error messages from the API if available
            const displayError = err.message || 'Connection interrupted';
            setError(displayError);

            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                    if (!last.content) {
                        last.content = `I apologize, but I encountered an error: ${displayError}. Please try again or check your configuration.`;
                    } else if (!last.content.includes('[Interrupted]')) {
                        last.content += `\n\n*(Note: This response was interrupted by an error: ${displayError})*`;
                    }
                }
                return updated;
            });

            // CRITICAL: Explicitly save the error state as the assistant message so it persists
            // We re-calculate it here to avoid race condition with the setState functional update
            const persistenceError = `I apologize, but I encountered an error: ${displayError}. Please try again or check your configuration.`;

            saveChatMessage({
                role: 'assistant',
                content: persistenceError,
                categoryId: options.categoryId
            }).catch(e => console.warn('Persistent error save failed:', e));
        } finally {
            setIsStreaming(false);
        }
    }, [messages, isStreaming]);

    const clearHistory = () => {
        setMessages([]);
        localStorage.removeItem('b2b_chat_local_backup');
    };

    return (
        <ChatContext.Provider value={{
            messages,
            isStreaming,
            isLoading,
            isSaving,
            error,
            sendMessage,
            clearHistory,
            loadHistory: () => setHistoryLoaded(false)
        }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) throw new Error('useChat must be used within a ChatProvider');
    return context;
};
